///
/// EarthBrowser
///
/// Created by Matt Giger on 1/10/2026
/// Copyright (c) 2026 Geodata Labs
///

import { TextureFrame } from "@/earthbrowser/datasets/TextureSeries"
import { ImageLoader } from "@/earthbrowser/utils/loaders/ImageLoader"
import { cCacheAgeOneWeek, type FetchPriority } from "@/earthbrowser/utils/loaders/FetchLoader"
import { datasetUrl } from "@/utils/datasetUrl"
import { type time, type timedelta } from "@stovokor/math/time"
import { ComponentType, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { Texture } from "@stovokor/shader/Texture"
import { type StovokorContext } from "@stovokor/StovokorContext"

/**
 * A TextureFrame specialized for radar data that loads textures as R8 grayscale
 * to use 1/4 the memory of RGBA textures.
 *
 * Pass wrapLongitude for global equirectangular frames so advected samples
 * wrap at the antimeridian instead of clamping.
 */
export class RadarTextureFrame extends TextureFrame {
	/** Optional RG32F displacement field carrying this frame toward the
	 *  frame at flowTargetUrl (UV units), used by the overlay shader to
	 *  advect precipitation while blending (see PrecipFlowTextureSeries) */
	public flowTexture?: Texture
	public flowTargetUrl?: string

	/** Generate mipmaps on upload. Off by default (overlay fields are
	 *  usually magnified); subclasses whose textures are minified at
	 *  globe scale turn this on to avoid shimmer */
	protected mipmaps: boolean = false

	private wrapLongitude: boolean

	constructor(context: StovokorContext, url: string, startTime: time, duration: timedelta, wrapLongitude: boolean = false) {
		super(context, url, startTime, duration)
		this.wrapLongitude = wrapLongitude
	}

	public override load(priority: FetchPriority = "auto"): void {
		if (this.loading || this.available) {
			return
		}
		this.loading = true
		if (this.texture === this.context.blankTexture) {
			this.createNewTexture()
		}
		this.textureLoader = new ImageLoader(datasetUrl(this.url), { lifetime: cCacheAgeOneWeek, priority })
		// Use grayscale loading for R8 texture format (1/4 memory)
		this.textureLoader.loadGrayscaleIntoTexture(this.texture, this.mipmaps)
			.then((success) => {
				this.available = success
				this.metadata = this.textureLoader?.metadata ?? {}
				this.loading = false
			})
			.catch(_e => { this.loading = false })
			.finally(() => { this.textureLoader = undefined })
	}

	public override purge(): void {
		this.flowTexture?.destroy()
		this.flowTexture = undefined
		this.flowTargetUrl = undefined
		super.purge()
	}

	protected override createNewTexture(): void {
		// Create texture configured for R8 format
		this.texture = new Texture(
			this.context,
			1,
			1,
			InternalFormat.R8,
			PixelFormat.Red,
			ComponentType.UnsignedByte,
			TextureFilter.LinearMipmapNearest,
			TextureFilter.Linear,
			this.wrapLongitude ? TextureWrap.Repeat : TextureWrap.ClampToEdge,
			TextureWrap.ClampToEdge
		)
	}
}
